import styled, { css } from 'styled-components'

import { PremiumCurrencyInfo } from '@root/entities/premium/ui'
import { useOptionalAtom } from '@root/shared/lib'
import { useTranslation } from '@root/shared/localization'
import { mediaMinTablet } from '@root/shared/ui-kit'

import { CheckCurrencySkeleton } from './check-currency-skeleton'

import type { CurrencySafetyCheck } from '@root/features/premium/check-currency/atoms'

type Props = {
  result: CurrencySafetyCheck | undefined
}

type Intent = 'success' | 'failed' | 'premium'

export const CheckCurrency: React.FC<Props> = ({ result }) => {
  const { t } = useTranslation()
  const premium = useOptionalAtom(result?.premiumInfo?.premium)

  if (!result) {
    return (
      <Root>
        {/* TODO: rework skeleton sizes */}
        <CheckCurrencySkeleton />
      </Root>
    )
  }

  const renderMessage = () => {
    if (premium) {
      return t('premium.check.alert.active')
    }
    return result.hasPassedSecurityChecks
      ? t('premium.check.alert.passed')
      : t('premium.check.alert.failed')
  }

  const intent = ((): Intent => {
    if (premium) {
      return 'premium'
    }
    return result.hasPassedSecurityChecks ? 'success' : 'failed'
  })()

  return (
    <Root>
      <PremiumCurrencyInfo
        premium={result.premiumInfo}
        currency={result.currency}
      />
      <Alert $intent={intent}>{renderMessage()}</Alert>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  gap: 16px;
  text-align: center;

  justify-items: center;
  width: 100%;

  padding-inline: 16px;

  ${mediaMinTablet(css`
    gap: 14px;
    min-height: 316px;
    padding-inline: 0;
  `)}
`

const Alert = styled.div<{ $intent: Intent }>`
  ${(props) => {
    switch (props.$intent) {
      case 'failed': {
        return css`
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(var(--errorAccent-9-rgb), 0.05) 50%,
            transparent 100%
          );
          color: var(--errorAccent-9);
        `
      }
      case 'success': {
        return css`
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(var(--successAccent-9-rgb), 0.05) 50%,
            transparent 100%
          );
          color: var(--successAccent-9);
        `
      }
      default: {
        return css`
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(var(--subscriptionButton-11-rgb), 0.07) 50%,
            transparent 100%
          );

          color: var(--subscriptionButton-9);
        `
      }
    }
  }}

  font-size: var(--typography-body-4-bold-font-size);
  display: grid;
  align-content: center;

  width: 100%;
  height: 50px;
  border-radius: 12px;

  font-weight: var(--typography-body-4-bold-font-weight);
  line-height: var(--typography-body-4-bold-line-height);
`
